import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import AdminLayout from "../components/AdminLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash, ArrowsClockwise, Globe, DownloadSimple, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  "Education", "Health & Wellness", "Environment", "Arts & Culture",
  "Community Development", "Youth Programs", "Senior Services",
  "Housing & Homelessness", "Food Security", "Economic Development",
  "Animal Welfare", "Disaster Relief", "Faith-Based"
];

const AdminGrants = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  const [filterSource, setFilterSource] = useState("");
  const [filterCategory, setFilterCategory] = useState("");

  // Export / Import (preview ↔ production sync)
  const [exporting, setExporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState("");
  const [importMode, setImportMode] = useState("skip"); // skip | overwrite
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const exportGrants = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/grants/export?source=manual`);
      const payload = res.data;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `flashgrants-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${payload.count} grants — file downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setImportJson(e.target.result);
    reader.readAsText(file);
  };

  const runImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const parsed = JSON.parse(importJson);
      // Accept either a raw {grants: [...]} payload OR an array directly
      const grantsArr = Array.isArray(parsed) ? parsed : (parsed.grants || []);
      if (!grantsArr.length) {
        toast.error("No grants found in the JSON payload");
        setImporting(false);
        return;
      }
      const res = await axios.post(`${API}/admin/grants/import`, {
        grants: grantsArr,
        mode: importMode,
      });
      setImportResult(res.data);
      const r = res.data;
      toast.success(`Done: ${r.imported} added · ${r.skipped} skipped · ${r.overwritten} overwritten`);
      fetchGrants();
    } catch (e) {
      const msg = e.message?.includes("JSON") ? "Invalid JSON — double-check the paste" : (e.response?.data?.detail || "Import failed");
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const emptyForm = {
    title: "", description: "", amount_min: "", amount_max: "",
    deadline: "", category: "", eligibility: "", requirements: "",
    focus_areas: "", funder_name: "", funder_type: "", funder_website: "",
    success_tips: ""
  };
  const [form, setForm] = useState(emptyForm);

  const fetchGrants = async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/grants?page=${page}&limit=20`;
      if (filterSource) url += `&source=${filterSource}`;
      if (filterCategory) url += `&category=${filterCategory}`;
      const res = await axios.get(url);
      setGrants(res.data.grants || []);
      setTotalPages(res.data.pages || 1);
      setTotalCount(res.data.total || 0);
    } catch (error) {
      console.error("Error fetching grants:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGrants(); }, [page, filterSource, filterCategory]);

  const resetForm = () => { setForm(emptyForm); setEditingGrant(null); };

  const openEditDialog = (grant) => {
    setEditingGrant(grant);
    setForm({
      title: grant.title || "",
      description: grant.description || "",
      amount_min: (grant.amount_min || "").toString(),
      amount_max: (grant.amount_max || "").toString(),
      deadline: (grant.deadline || "").split("T")[0],
      category: grant.category || "",
      eligibility: grant.eligibility || "",
      requirements: (grant.requirements || []).join("\n"),
      focus_areas: (grant.focus_areas || []).join(", "),
      funder_name: grant.funder_name || "",
      funder_type: grant.funder_type || "",
      funder_website: grant.funder_website || "",
      success_tips: (grant.success_tips || []).join("\n")
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.description || !form.deadline || !form.category) {
      toast.error("Title, description, deadline, and category are required");
      return;
    }
    const payload = {
      title: form.title,
      description: form.description,
      amount_min: parseFloat(form.amount_min) || 0,
      amount_max: parseFloat(form.amount_max) || 0,
      deadline: form.deadline,
      category: form.category,
      eligibility: form.eligibility,
      requirements: form.requirements.split("\n").filter(r => r.trim()),
      focus_areas: form.focus_areas.split(",").map(f => f.trim()).filter(f => f),
      funder_name: form.funder_name,
      funder_type: form.funder_type,
      funder_website: form.funder_website,
      success_tips: form.success_tips.split("\n").filter(t => t.trim())
    };

    try {
      if (editingGrant) {
        await axios.put(`${API}/admin/grants/${editingGrant.grant_id}`, payload);
        toast.success("Grant updated");
      } else {
        await axios.post(`${API}/admin/grants`, payload);
        toast.success("Grant created");
      }
      setDialogOpen(false);
      resetForm();
      fetchGrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save grant");
    }
  };

  const deleteGrant = async (grantId) => {
    if (!confirm("Delete this grant permanently?")) return;
    try {
      await axios.delete(`${API}/admin/grants/${grantId}`);
      toast.success("Grant deleted");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to delete grant");
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/admin/grants/sync-grantsgov`);
      toast.success(res.data.message || "Sync started!");
      setTimeout(() => { fetchGrants(); setSyncing(false); }, 15000);
    } catch (error) {
      toast.error("Sync failed");
      setSyncing(false);
    }
  };

  const sourceBadge = (source) => {
    if (source === "grants.gov") return <span className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-sm px-1.5 py-0.5">Grants.gov</span>;
    if (source === "manual") return <span className="text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-sm px-1.5 py-0.5">Manual</span>;
    return <span className="text-[10px] bg-zinc-500/10 text-zinc-400 border border-zinc-500/20 rounded-sm px-1.5 py-0.5">Legacy</span>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-grants-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Grant Management</h1>
            <p className="text-zinc-400 mt-1">{totalCount} grants total — create, edit, and sync real grant data</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={exportGrants}
              disabled={exporting}
              className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
              data-testid="export-grants-btn"
              title="Download all manual grants as JSON. Use this to move grants from preview to production."
            >
              <DownloadSimple className="w-4 h-4 mr-2" />
              {exporting ? "Exporting..." : "Export"}
            </Button>
            <Dialog open={importDialogOpen} onOpenChange={(open) => { setImportDialogOpen(open); if (!open) { setImportJson(""); setImportResult(null); } }}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-violet-500/30 text-violet-300 hover:bg-violet-500/10"
                  data-testid="import-grants-btn"
                  title="Import grants from a JSON file (typically exported from preview)."
                >
                  <UploadSimple className="w-4 h-4 mr-2" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Import grants from JSON</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Paste a JSON payload (or upload a file) exported from another FlashGrants environment.
                    Idempotent — duplicates by <code className="text-emerald-300">grant_id</code> are handled by the mode you choose below.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={(e) => handleImportFile(e.target.files?.[0])}
                      className="text-xs text-zinc-400"
                      data-testid="import-file-input"
                    />
                    <span className="text-xs text-zinc-600">or paste below ↓</span>
                  </div>
                  <Textarea
                    value={importJson}
                    onChange={(e) => setImportJson(e.target.value)}
                    placeholder='{"grants": [{"grant_id": "...", "title": "...", ...}]}'
                    rows={8}
                    className="bg-zinc-900 border-zinc-800 text-xs font-mono"
                    data-testid="import-json-textarea"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-zinc-500">If grant_id already exists:</span>
                      <select
                        value={importMode}
                        onChange={(e) => setImportMode(e.target.value)}
                        className="bg-zinc-900 border border-zinc-800 rounded-sm px-2 py-1 text-xs text-zinc-200"
                        data-testid="import-mode-select"
                      >
                        <option value="skip">Skip (safe)</option>
                        <option value="overwrite">Overwrite</option>
                      </select>
                    </div>
                    <Button
                      onClick={runImport}
                      disabled={importing || !importJson.trim()}
                      className="bg-violet-500 hover:bg-violet-400 text-black"
                      data-testid="run-import-btn"
                    >
                      {importing ? "Importing..." : "Run import"}
                    </Button>
                  </div>
                  {importResult && (
                    <div className="rounded-sm border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs space-y-1" data-testid="import-result">
                      <p className="font-semibold text-emerald-300">Import complete</p>
                      <p className="text-zinc-300">
                        {importResult.imported} imported · {importResult.skipped} skipped · {importResult.overwritten} overwritten
                        {importResult.errors_count > 0 && <span className="text-rose-300"> · {importResult.errors_count} errors</span>}
                      </p>
                      {importResult.errors_count > 0 && (
                        <details className="text-zinc-400">
                          <summary className="cursor-pointer hover:text-zinc-200">Show errors</summary>
                          <pre className="mt-2 text-[10px] text-rose-300 overflow-auto">{JSON.stringify(importResult.errors, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              onClick={triggerSync}
              disabled={syncing}
              className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
              data-testid="sync-grantsgov-btn"
            >
              <ArrowsClockwise className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? "Syncing..." : "Sync Grants.gov"}
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="create-grant-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Grant
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-950 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingGrant ? "Edit Grant" : "Add Real Grant"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input value={form.title} onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                      placeholder="e.g., Sisters of Charity Foundation Community Grant" className="bg-zinc-900 border-zinc-800" data-testid="input-grant-title" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Min Amount ($)</Label>
                      <Input type="number" value={form.amount_min} onChange={(e) => setForm(p => ({ ...p, amount_min: e.target.value }))}
                        placeholder="500" className="bg-zinc-900 border-zinc-800" data-testid="input-amount-min" />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Amount ($)</Label>
                      <Input type="number" value={form.amount_max} onChange={(e) => setForm(p => ({ ...p, amount_max: e.target.value }))}
                        placeholder="5000" className="bg-zinc-900 border-zinc-800" data-testid="input-amount-max" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Deadline *</Label>
                      <Input type="date" value={form.deadline} onChange={(e) => setForm(p => ({ ...p, deadline: e.target.value }))}
                        className="bg-zinc-900 border-zinc-800" data-testid="input-deadline" />
                    </div>
                    <div className="space-y-2">
                      <Label>Category *</Label>
                      <Select value={form.category} onValueChange={(v) => setForm(p => ({ ...p, category: v }))}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-category"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Funder Name</Label>
                    <Input value={form.funder_name} onChange={(e) => setForm(p => ({ ...p, funder_name: e.target.value }))}
                      placeholder="e.g., Sisters of Charity Foundation of South Carolina" className="bg-zinc-900 border-zinc-800" data-testid="input-funder-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Funder Type</Label>
                      <Select value={form.funder_type} onValueChange={(v) => setForm(p => ({ ...p, funder_type: v }))}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="foundation">Foundation</SelectItem>
                          <SelectItem value="government">Government</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="community">Community</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Funder Website</Label>
                      <Input value={form.funder_website} onChange={(e) => setForm(p => ({ ...p, funder_website: e.target.value }))}
                        placeholder="https://..." className="bg-zinc-900 border-zinc-800" data-testid="input-funder-website" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
                      placeholder="Describe the grant opportunity..." className="bg-zinc-900 border-zinc-800 min-h-24" data-testid="input-description" />
                  </div>
                  <div className="space-y-2">
                    <Label>Eligibility</Label>
                    <Textarea value={form.eligibility} onChange={(e) => setForm(p => ({ ...p, eligibility: e.target.value }))}
                      placeholder="Who is eligible?" className="bg-zinc-900 border-zinc-800" data-testid="input-eligibility" />
                  </div>
                  <div className="space-y-2">
                    <Label>Requirements (one per line)</Label>
                    <Textarea value={form.requirements} onChange={(e) => setForm(p => ({ ...p, requirements: e.target.value }))}
                      placeholder="501(c)(3) status&#10;Operating in SC" className="bg-zinc-900 border-zinc-800" data-testid="input-requirements" />
                  </div>
                  <div className="space-y-2">
                    <Label>Focus Areas (comma-separated)</Label>
                    <Input value={form.focus_areas} onChange={(e) => setForm(p => ({ ...p, focus_areas: e.target.value }))}
                      placeholder="Youth, Education, Health" className="bg-zinc-900 border-zinc-800" data-testid="input-focus-areas" />
                  </div>
                  <div className="space-y-2">
                    <Label>Success Tips (one per line)</Label>
                    <Textarea value={form.success_tips} onChange={(e) => setForm(p => ({ ...p, success_tips: e.target.value }))}
                      placeholder="Review funder priorities&#10;Include measurable outcomes" className="bg-zinc-900 border-zinc-800" />
                  </div>
                  <Button onClick={handleSubmit} className="w-full bg-emerald-500 text-black font-bold hover:bg-emerald-400 rounded-sm" data-testid="submit-grant-btn">
                    {editingGrant ? "Update Grant" : "Add Grant"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={filterSource} onValueChange={(v) => { setFilterSource(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="filter-source">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="grants.gov">Grants.gov</SelectItem>
              <SelectItem value="manual">Manual</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={(v) => { setFilterCategory(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800" data-testid="filter-category">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <Card className="bg-zinc-950 border-zinc-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>
            ) : grants.length === 0 ? (
              <div className="p-12 text-center text-zinc-500">No grants found. Click &quot;Add Grant&quot; or &quot;Sync Grants.gov&quot; to get started.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead>Source</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Funder</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Deadline</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {grants.map((grant) => (
                    <TableRow key={grant.grant_id} className="border-zinc-800">
                      <TableCell>{sourceBadge(grant.source)}</TableCell>
                      <TableCell className="font-medium max-w-xs truncate">
                        {grant.source === "grants.gov" && grant.source_url ? (
                          <a href={grant.source_url} target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 flex items-center gap-1">
                            {grant.title} <Globe className="w-3 h-3 flex-shrink-0" />
                          </a>
                        ) : grant.title}
                      </TableCell>
                      <TableCell><span className="badge-emerald rounded-sm px-2 py-0.5 text-xs">{grant.category}</span></TableCell>
                      <TableCell className="text-zinc-400 text-xs max-w-[120px] truncate">{grant.funder_name || "—"}</TableCell>
                      <TableCell className="text-emerald-400 font-mono text-xs">
                        {grant.amount_min || grant.amount_max
                          ? `$${(grant.amount_min || 0).toLocaleString()}–$${(grant.amount_max || 0).toLocaleString()}`
                          : "See listing"}
                      </TableCell>
                      <TableCell className="text-zinc-400 text-xs">{grant.deadline ? new Date(grant.deadline + "T00:00:00").toLocaleDateString() : "Open"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(grant)} data-testid={`edit-grant-${grant.grant_id}`}>
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteGrant(grant.grant_id)} className="text-red-400 hover:text-red-300" data-testid={`delete-grant-${grant.grant_id}`}>
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="border-zinc-800">Previous</Button>
            <span className="text-sm text-zinc-400 px-4">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="border-zinc-800">Next</Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminGrants;
